import React, { useState, useEffect } from 'react';
import { 
  Award, 
  TrendingUp, 
  Lock, 
  Unlock, 
  Info,
  Crown,
  Star,
  Zap,
  Target
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import TierSystemModal from './TierSystemModal';
import { Progress } from './ui/progress';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const TIER_ICONS = {
  1: Target,
  2: Star,
  3: Zap,
  4: Crown
};

const TierProgressCard = () => {
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAdvancement, setShowAdvancement] = useState(false);

  useEffect(() => {
    fetchTierInfo();
  }, []);

  const fetchTierInfo = async () => {
    try {
      const response = await axios.get(`${API}/creator/tier-info`);
      setTierData(response.data);
      
      // Check for tier advancement
      if (response.data.advancement?.advanced) {
        setShowAdvancement(true);
        setTimeout(() => setShowAdvancement(false), 5000);
      }
    } catch (error) {
      console.error('Error fetching tier info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !tierData) {
    return (
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 animate-pulse">
        <div className="h-24 bg-gray-800 rounded"></div>
      </div>
    );
  }

  const TierIcon = TIER_ICONS[tierData.tier_level];
  const nextTierInfo = tierData.progress;

  return (
    <>
      {/* Tier Advancement Notification */}
      {showAdvancement && tierData.advancement?.advanced && (
        <div className="mb-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-6 text-white animate-bounce">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Crown className="w-10 h-10" />
            </div>
            <div>
              <h3 className="text-2xl font-black mb-1">🎉 Tier Advanced!</h3>
              <p className="text-lg">{tierData.advancement.message}</p>
              <p className="text-sm opacity-90 mt-1">
                New features unlocked: {tierData.advancement.new_features.join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Tier Card */}
      <div 
        className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg border-2 overflow-hidden"
        style={{ borderColor: tierData.tier_color }}
      >
        {/* Header with Tier Badge */}
        <div 
          className="p-6 relative"
          style={{ 
            background: `linear-gradient(135deg, ${tierData.tier_color}20 0%, transparent 100%)`
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              {/* Tier Icon */}
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${tierData.tier_color}30` }}
              >
                <TierIcon 
                  className="w-10 h-10" 
                  style={{ color: tierData.tier_color }}
                />
              </div>

              {/* Tier Info */}
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <h3 
                    className="text-2xl font-black"
                    style={{ color: tierData.tier_color }}
                  >
                    {tierData.tier_name}
                  </h3>
                  <span className="bg-gray-800 text-gray-300 text-xs font-bold px-2 py-1 rounded">
                    Tier {tierData.tier_level}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">{tierData.tier_description}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <Award className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-500">
                    {tierData.points.toLocaleString()} points earned
                  </span>
                </div>
              </div>
            </div>

            {/* Info Button */}
            <Button
              onClick={() => setShowModal(true)}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <Info className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Progress Section */}
        {!nextTierInfo.at_max_tier && (
          <div className="p-6 bg-gray-900/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-bold text-white">
                  Progress to {nextTierInfo.next_tier_name}
                </span>
              </div>
              <span className="text-sm font-bold text-orange-500">
                {nextTierInfo.percentage}%
              </span>
            </div>

            {/* Progress Bar */}
            <Progress 
              value={nextTierInfo.percentage} 
              className="h-3 mb-3"
              style={{
                backgroundColor: '#1F2937',
              }}
            />

            {/* Points Needed */}
            <p className="text-xs text-gray-400">
              <span className="font-bold text-white">
                {nextTierInfo.points_to_next.toLocaleString()}
              </span> more points to unlock {nextTierInfo.next_tier_name} features
            </p>
          </div>
        )}

        {tierData.tier_level === 4 && (
          <div className="p-6 bg-gradient-to-r from-red-900/20 to-orange-900/20">
            <div className="flex items-center space-x-2 text-orange-400">
              <Crown className="w-5 h-5" />
              <span className="font-bold">Maximum tier achieved! You're a Trap Original!</span>
            </div>
          </div>
        )}

        {/* Quick Features Preview */}
        <div className="p-6 bg-gray-900 border-t border-gray-800">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Your Unlocked Features:</h4>
          <div className="grid grid-cols-2 gap-2">
            {tierData.features.map((feature) => (
              <div 
                key={feature}
                className="flex items-center space-x-2 text-xs text-gray-300"
              >
                <Unlock className="w-3 h-3 text-green-500" />
                <span className="capitalize">{feature.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tier System Modal */}
      <TierSystemModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)}
        currentTier={tierData.tier_level}
      />
    </>
  );
};

export default TierProgressCard;
